import React, { useMemo, ReactNode } from 'react';
import { useRBACStore, OrganizationType, UserRole, FeaturePermissionType, PermissionType } from '@/stores/rbac-store';

/**
 * Enhanced RBAC Permissions Context Provider
 * 
 * This context handles all permission checking logic for the RBAC system.
 * It's split from the data context (rbac-data-context.tsx) for:
 * - Better performance (memoized permission functions)
 * - Separation of concerns (permission logic vs. data fetching)
 * - Enhanced audit logging for security compliance
 * 
 * Features:
 * - Memoized permission checking functions for performance
 * - Audit logging for all permission checks
 * - Support for basic permissions (view, edit, delete, create)
 * - Support for feature-specific permissions
 * - Role-based access control with organizational hierarchy
 */

// Permissions context interface for permission checking functions
export interface RBACPermissionsContextValue {
  userRole: UserRole | null;
  canView: (orgId: string, type: OrganizationType) => boolean;
  canEdit: (orgId: string, type: OrganizationType) => boolean;
  canDelete: (orgId: string, type: OrganizationType) => boolean;
  canAccessFeature: (feature: FeaturePermissionType) => boolean;
  checkPermissionWithAudit: (orgId: string, type: OrganizationType, permission: PermissionType) => boolean;
  hasRole: (role: UserRole) => boolean;
  isLoading: boolean;
  error: unknown;
}

const RBACPermissionsContext = React.createContext<RBACPermissionsContextValue | null>(null);

// Export context for use in hook
export { RBACPermissionsContext };

interface RBACPermissionsProviderProps {
  children: ReactNode;
}

/**
 * RBACPermissionsProvider handles all permission checking logic
 * Separated from data fetching for better performance and caching
 */
export const RBACPermissionsProvider = ({ children }: RBACPermissionsProviderProps) => {
  const { 
    getUserRole, 
    canAccessOrganization, 
    canEditOrganization, 
    canDeleteOrganization,
    canAccessFeature,
    checkPermissionWithAudit,
    isLoading,
    error
  } = useRBACStore();
  
  const userRole = getUserRole();

  // Memoize permission functions for performance
  const permissions = useMemo(() => ({
    userRole,
    canView: (orgId: string, type: OrganizationType) => canAccessOrganization(orgId, type),
    canEdit: (orgId: string, type: OrganizationType) => canEditOrganization(orgId, type),
    canDelete: (orgId: string, type: OrganizationType) => canDeleteOrganization(orgId, type),
    canAccessFeature: (feature: FeaturePermissionType) => canAccessFeature(feature),
    checkPermissionWithAudit: (orgId: string, type: OrganizationType, permission: PermissionType) => 
      checkPermissionWithAudit(orgId, type, permission),
    hasRole: (role: UserRole) => userRole === role,
    isLoading,
    error
  }), [
    userRole, 
    canAccessOrganization, 
    canEditOrganization, 
    canDeleteOrganization,
    canAccessFeature,
    checkPermissionWithAudit,
    isLoading,
    error
  ]);

  return (
    <RBACPermissionsContext.Provider value={permissions}>
      {children}
    </RBACPermissionsContext.Provider>
  );
};